import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { faCircleQuestion, faRefresh, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CampaignMatchRequestParams,
    createCampaignMatch,
    updateCampaignMatch,
} from 'api/campaigns';
import { deleteCampaignMatchMediaByUrl, uploadCampaignMatchMedia } from 'api/media';
import { AxiosResponse } from 'axios';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import * as Tooltip from 'components/tooltip';
import { Upload, UploadRef } from 'components/upload';
import { IntercomProductTourTargets } from 'constants/intercom';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useCurrentCampaignEvent } from 'customHooks/useCurrentCampaignEvent';
import { FilePondFile } from 'filepond';
import {
    dateToServerDateTime,
    getClosestThirtyMinuteDateTime,
    isoDateFromServer,
} from 'helpers/dates';
import { DateTime } from 'luxon';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { CampaignMatch } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface CampaignMatchFormValues {
    name: string;
    description: string | null;
    enabled: boolean;
    amount: string;
    type: CampaignMatch['type'];
    showMatchAmount: boolean;
    showCountdown: boolean;
    matchOfflineDonations: boolean;
    includeInRaisedAmount: boolean;
    startDate: [DateTime];
    hasEndDate: boolean;
    endDate: [DateTime];
}

const CampaignToolsMatchSettings = () => {
    const toast = useToast();
    const { accountId } = useCurrentAccount();
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign?.id ?? 0;
    const match = campaign?.match;
    const {
        matchFulfilledNotificationDismissedCampaignIds,
        setMatchFulfilledNotificationDismissedCampaignIds,
    } = useLocalStorage(state => {
        return {
            setMatchFulfilledNotificationDismissedCampaignIds:
                state.setMatchFulfilledNotificationDismissedCampaignIds,
            matchFulfilledNotificationDismissedCampaignIds:
                state.matchFulfilledNotificationDismissedCampaignIds,
        };
    });
    const hasDismissedMatchFulfilledNotification =
        matchFulfilledNotificationDismissedCampaignIds.find(
            dismissedId => dismissedId === campaignId,
        ) !== undefined;
    const ref = useRef<UploadRef>(null);
    const [files, setFiles] = useState<FilePondFile[]>([]);
    const { data: event } = useCurrentCampaignEvent();

    const eventStartDate = event?.start_at
        ? isoDateFromServer(event.start_at)
        : getClosestThirtyMinuteDateTime();

    const eventEndDate = event?.end_at
        ? isoDateFromServer(event.end_at)
        : getClosestThirtyMinuteDateTime().plus({ days: 1 });

    const methods = Form.useGivebutterForm<CampaignMatchFormValues>({
        defaultValues: {
            name: match?.name ?? '',
            description: match?.description ?? '',
            enabled: match?.enabled ?? false,
            amount: match?.amount ?? '',
            type: match?.type ?? '1:1',
            showMatchAmount: match?.show_public_amount ?? true,
            showCountdown: match?.show_public_countdown ?? true,
            matchOfflineDonations: match?.match_offline_donations ?? false,
            includeInRaisedAmount: match?.include_in_raised_amount ?? true,
            startDate: [match?.start_at ? isoDateFromServer(match?.start_at) : eventStartDate],
            hasEndDate: !!match?.end_at || (!match && !!event?.end_at),
            endDate: [match?.end_at ? isoDateFromServer(match?.end_at) : eventEndDate],
        },
    });
    const { watch, control, path, handleSubmit, setError } = methods;
    const [enabled, hasEndDate] = watch(['enabled', 'hasEndDate']);

    const sharedRequestOptions = {
        onSuccess: (response: AxiosResponse<CampaignMatch>) => {
            if (
                hasDismissedMatchFulfilledNotification &&
                Number(response.data.amount_remaining) > 0
            ) {
                setMatchFulfilledNotificationDismissedCampaignIds(
                    matchFulfilledNotificationDismissedCampaignIds.filter(
                        dismissedId => dismissedId !== campaignId,
                    ),
                );
            }

            return uploadImage(Number(response.data.id));
        },
        onError: (error: GivebutterAxiosError) => {
            const errors = error.response?.data.errors;

            if (errors) {
                for (const field in errors) {
                    setError(field as keyof CampaignMatchFormValues, {
                        message: errors[field][0],
                    });
                }
            }

            toast.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    };

    const { mutate: createMatch } = useMutation({
        mutationFn: (request: CampaignMatchRequestParams) =>
            createCampaignMatch(campaignId, request),
        ...sharedRequestOptions,
    });

    const { mutate: updateMatch } = useMutation({
        mutationFn: (request: CampaignMatchRequestParams) =>
            updateCampaignMatch(campaignId, match?.id ?? 0, request),
        ...sharedRequestOptions,
    });

    const { mutate: uploadImage } = useMutation({
        mutationFn: (matchId: number) => {
            if (files.length === 0) {
                return Promise.resolve(null);
            }
            return uploadCampaignMatchMedia(campaignId, matchId, files[0].file as File);
        },
        onSuccess: () => {
            ref.current?._pond?.removeFiles();
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
            if (enabled) {
                toast.success(
                    <div className="text-left">
                        <div className="font-medium">Match settings saved</div>
                        <div>Your campaign is now updated for donation matching.</div>
                    </div>,
                    { size: 'md' },
                );
            } else {
                toast.warning(
                    <div className="text-left">
                        <div className="font-medium">Matching disabled</div>
                        <div>Your campaign is no longer matching donations.</div>
                    </div>,
                    { size: 'md' },
                );
            }
        },
        onError: (error: GivebutterAxiosError) => {
            toast.error(error.response?.data.message ?? 'Unknown error occurred');
        },
    });

    const submit = (data: CampaignMatchFormValues) => {
        const request = toRequest(data);
        if (match) {
            return updateMatch(request);
        }
        return createMatch(request);
    };

    const { mutate: deletePhoto } = useMutation({
        mutationFn: (url: string) =>
            deleteCampaignMatchMediaByUrl(campaignId, Number(match?.id), url),
        onSuccess: () => {
            toast.success('Deleted match photo!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.campaign(campaignId),
            });
        },
        onError: (e: GivebutterAxiosError) =>
            toast.error(e.response?.data?.message ?? 'Failed to delete match photo'),
    });

    return (
        <Form.GivebutterFormProvider {...methods}>
            <form onSubmit={e => handleSubmit(submit)(e)}>
                <SettingsCard.Root>
                    <SettingsCard.LeftSide>
                        <SettingsCard.Heading
                            data-intercom-target={
                                IntercomProductTourTargets.CampaignToolsMatchesTitle
                            }
                        >
                            Matching
                        </SettingsCard.Heading>
                        <p className="text-sm text-gray-600">
                            Add donation matching and drive more support to your campaign.
                        </p>
                    </SettingsCard.LeftSide>
                    <SettingsCard.RightSide>
                        <div className="flex flex-col gap-4">
                            <Form.Container className={'flex-row justify-between'}>
                                <div className={'flex flex-col gap-2'}>
                                    <Form.Label
                                        name={path('enabled')}
                                        className="flex flex-wrap gap-2 text-base font-medium"
                                    >
                                        Enable matching
                                    </Form.Label>
                                    <p className={'text-sm text-gray-600'}>
                                        When enabled, you can add a match to your campaign allowing
                                        donations to be matched automatically until a match is
                                        exhausted.
                                    </p>
                                </div>
                                <Form.Switch
                                    name={path('enabled')}
                                    control={control}
                                    onCheckedChange={() => {
                                        if (match) {
                                            return updateMatch({
                                                enabled: !enabled,
                                            } as CampaignMatchRequestParams);
                                        }
                                    }}
                                />
                            </Form.Container>
                            {enabled && (
                                <>
                                    <div className="flex gap-2">
                                        <Form.Container className="basis-3/5">
                                            <Form.Label name={path('amount')}>
                                                Match amount
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                name={path('amount')}
                                                control={control}
                                                inputRootProps={{
                                                    mask: 'decimal',
                                                }}
                                            >
                                                <Form.TextInputForm placeholder={'0.00'} />
                                            </Form.TextInputRoot>
                                            <Form.UseFormError name={'amount'} />
                                            <p className="text-sm text-gray-600">
                                                Maximum amount available to be matched.
                                            </p>
                                        </Form.Container>
                                        <Form.Container className="basis-2/5">
                                            <Form.Label name={path('type')}>
                                                Match ratio{' '}
                                                <Tooltip.Root>
                                                    <Tooltip.Trigger asChild>
                                                        <FontAwesomeIcon
                                                            icon={faCircleQuestion}
                                                            className="text-gray-500"
                                                        />
                                                    </Tooltip.Trigger>
                                                    <Tooltip.Portal>
                                                        <Tooltip.Content inverse>
                                                            <p>
                                                                Ratio by which a donation will be
                                                                matched. For ex., a 2x (1:1) match
                                                                will result in a $100 match on a
                                                                $100 donation. A 3x (2:1) match will
                                                                result in a $200 match on a $100
                                                                donation.
                                                            </p>
                                                            <Tooltip.Arrow inverse />
                                                        </Tooltip.Content>
                                                    </Tooltip.Portal>
                                                </Tooltip.Root>
                                            </Form.Label>
                                            <Form.SingleSelect control={control} name="type">
                                                <SingleSelect.Trigger aria-label="Match ratio" />
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="1:1">
                                                        <SingleSelect.ItemText>
                                                            2x (1:1)
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="2:1">
                                                        <SingleSelect.ItemText>
                                                            3x (2:1)
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="3:1">
                                                        <SingleSelect.ItemText>
                                                            4x (3:1)
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <div>
                                            <Form.UseFormCheckboxInput
                                                name={path('showMatchAmount')}
                                                control={control}
                                            />
                                        </div>
                                        <div>
                                            <Form.Label
                                                name={path('showMatchAmount')}
                                                className="align-top"
                                            >
                                                Show match amount
                                            </Form.Label>
                                            <p className="text-sm text-gray-600">
                                                Show the total match amount on your campaign.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-2">
                                        <div>
                                            <Form.UseFormCheckboxInput
                                                name={path('matchOfflineDonations')}
                                                control={control}
                                            />
                                        </div>
                                        <div>
                                            <Form.Label
                                                name={path('matchOfflineDonations')}
                                                className="align-top"
                                            >
                                                Match offline donations
                                            </Form.Label>
                                            <p className="text-sm text-gray-600">
                                                Apply a matching donation to check or cash donations
                                                that are attributed to this campaign. This includes
                                                check donations made by donors or offline donations
                                                that are manually added.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <div>
                                            <Form.UseFormCheckboxInput
                                                name={path('includeInRaisedAmount')}
                                                control={control}
                                            />
                                        </div>
                                        <div>
                                            <Form.Label
                                                name={path('includeInRaisedAmount')}
                                                className="align-top"
                                            >
                                                Include in goal bar progress
                                            </Form.Label>
                                            <p className="text-sm text-gray-600">
                                                Increase the goal bar with both the donation and the
                                                matched amount.
                                            </p>
                                        </div>
                                    </div>
                                    <Separator />
                                    <Form.Container>
                                        <Form.Label name={path('startDate')}>
                                            Start date
                                            <Form.LabelAsterisk />
                                        </Form.Label>
                                        <Form.UseFormDateTimePicker
                                            control={control}
                                            name={path('startDate')}
                                            allowRange={false}
                                            startDateTimeProps={{
                                                startInputProps: {
                                                    placeholder: 'Select date & time',
                                                },
                                            }}
                                        />
                                        <Form.UseFormError name={'start_date'} />
                                    </Form.Container>

                                    <div className="flex flex-col gap-2">
                                        <Form.Container className={'flex-row justify-between'}>
                                            <div className={'flex flex-col'}>
                                                <Form.Label name={path('hasEndDate')}>
                                                    End date
                                                </Form.Label>
                                                <p className={'text-sm text-gray-600'}>
                                                    Add an end date and countdown to your page.
                                                </p>
                                            </div>
                                            <Form.Switch
                                                name={path('hasEndDate')}
                                                control={control}
                                            />
                                        </Form.Container>

                                        {hasEndDate && (
                                            <div className="flex flex-col gap-4">
                                                <Form.Container>
                                                    <Form.UseFormDateTimePicker
                                                        control={control}
                                                        name={path('endDate')}
                                                        allowRange={false}
                                                        startDateTimeProps={{
                                                            startInputProps: {
                                                                placeholder: 'Select date & time',
                                                            },
                                                        }}
                                                    />
                                                    <Form.UseFormError name={'end_date'} />
                                                </Form.Container>

                                                <div className="flex items-start gap-2">
                                                    <div>
                                                        <Form.UseFormCheckboxInput
                                                            name={path('showCountdown')}
                                                            control={control}
                                                        />
                                                    </div>
                                                    <div>
                                                        <Form.Label
                                                            name={path('showCountdown')}
                                                            className="align-top"
                                                        >
                                                            Show countdown
                                                        </Form.Label>
                                                        <p className="text-sm text-gray-600">
                                                            Show how much time is remaining for the
                                                            match on your campaign.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div className="text-sm">
                                            <Link
                                                to={`/accounts/${accountId}/campaigns/${campaignId}/settings`}
                                            >
                                                Edit time zone
                                            </Link>
                                        </div>
                                    </div>
                                    <Separator />
                                    <Form.Container>
                                        <Form.Label name={path('name')}>
                                            Match name
                                            <Form.LabelAsterisk />
                                        </Form.Label>
                                        <Form.TextInputRoot name={path('name')} control={control}>
                                            <Form.TextInputForm placeholder={'Match name'} />
                                        </Form.TextInputRoot>
                                        <Form.UseFormError name={path('name')} />
                                        <p className="text-sm text-gray-600">
                                            Publicly recognize your match on your campaign. This is
                                            shown on the receipt.
                                        </p>
                                    </Form.Container>
                                    <Form.Container>
                                        <div className="flex items-center gap-2">
                                            <Label>Upload an image</Label>
                                        </div>
                                        <div className="flex gap-6">
                                            {match?.cover?.url && (
                                                <div className="flex flex-col gap-3">
                                                    <div>
                                                        <img
                                                            src={match?.cover?.url}
                                                            className="max-h-[10rem] max-w-[13.75rem] rounded-lg"
                                                            alt="campaign donation match cover"
                                                        />
                                                    </div>
                                                    <div className="flex gap-3">
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            color="neutral"
                                                            onClick={() => ref.current?.browse()}
                                                        >
                                                            <FontAwesomeIcon icon={faRefresh} />
                                                            Replace
                                                        </Button>

                                                        <AlertDialog.Root type="error">
                                                            <AlertDialog.Trigger asChild>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    color="error"
                                                                >
                                                                    <FontAwesomeIcon
                                                                        icon={faTrash}
                                                                    />
                                                                    Delete
                                                                </Button>
                                                            </AlertDialog.Trigger>
                                                            <AlertDialog.Portal>
                                                                <AlertDialog.Overlay />
                                                                <AlertDialog.Content>
                                                                    <AlertDialog.Header>
                                                                        <AlertDialog.Icon />
                                                                        <AlertDialog.Close />
                                                                    </AlertDialog.Header>
                                                                    <AlertDialog.Body>
                                                                        <AlertDialog.Title>
                                                                            Delete image?
                                                                        </AlertDialog.Title>
                                                                        <AlertDialog.Description>
                                                                            Deleting this image is a
                                                                            permanent action and
                                                                            cannot be undone.
                                                                        </AlertDialog.Description>
                                                                    </AlertDialog.Body>
                                                                    <AlertDialog.Footer>
                                                                        <AlertDialog.Cancel>
                                                                            Cancel
                                                                        </AlertDialog.Cancel>
                                                                        <AlertDialog.Action
                                                                            onClick={() =>
                                                                                deletePhoto(
                                                                                    match?.cover
                                                                                        ?.url ?? '',
                                                                                )
                                                                            }
                                                                        >
                                                                            Delete
                                                                        </AlertDialog.Action>
                                                                    </AlertDialog.Footer>
                                                                </AlertDialog.Content>
                                                            </AlertDialog.Portal>
                                                        </AlertDialog.Root>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex w-full flex-col gap-3">
                                                <Upload
                                                    className={'min-h-[12rem]'}
                                                    ref={ref}
                                                    acceptedFileTypes={['image/*']}
                                                    allowReorder={false}
                                                    allowReplace={true}
                                                    allowMultiple={false}
                                                    maxFiles={1}
                                                    strategy={'remote'}
                                                    onupdatefiles={files => {
                                                        setFiles(files);
                                                    }}
                                                    onremovefile={(_, file) =>
                                                        deletePhoto(file.getMetadata('url'))
                                                    }
                                                />
                                                <p className="text-sm text-gray-600">
                                                    Upload 222 x 160 for best resolution.
                                                </p>
                                            </div>
                                        </div>
                                    </Form.Container>
                                    <Form.Container>
                                        <Form.Label name={path('description')}>
                                            Match description
                                        </Form.Label>
                                        <Form.UseFormTextArea
                                            name={path('description')}
                                            control={control}
                                            rows={5}
                                            placeholder={'Enter match description here'}
                                        />
                                        <p className={'text-sm text-gray-600'}>
                                            Provide a public description about your match on your
                                            campaign.
                                        </p>
                                    </Form.Container>
                                    <div>
                                        <Form.SubmitButton>Save</Form.SubmitButton>
                                    </div>
                                </>
                            )}
                        </div>
                    </SettingsCard.RightSide>
                </SettingsCard.Root>
            </form>
        </Form.GivebutterFormProvider>
    );
};

const toRequest = (data: CampaignMatchFormValues): CampaignMatchRequestParams => {
    return {
        name: data.name.trim(),
        description: data.description ? data.description.trim() : null,
        enabled: data.enabled,
        amount: unmaskNumber(data.amount),
        type: data.type,
        start_at: dateToServerDateTime(data.startDate[0]),
        end_at: data.hasEndDate ? dateToServerDateTime(data.endDate[0]) : null,
        show_public_amount: data.showMatchAmount,
        show_public_countdown: data.showCountdown,
        match_offline_donations: data.matchOfflineDonations,
        include_in_raised_amount: data.includeInRaisedAmount,
    };
};

export { CampaignToolsMatchSettings };
